// Mapping between quotes, quote templates and the quote form (#810).
import type { QuoteRow } from "@/hooks/useQuotes";
import type { CreateTemplateInput, QuoteTemplateItemRow, QuoteTemplateRow } from "@/hooks/useQuoteTemplates";
import type { LineItemDraft } from "@/components/quotes/QuoteLineItemRow";
import { lineTotal } from "@/lib/quote-totals";
import { effectiveServiceType } from "@/types/quotes";

export interface TemplateDetails {
  name: string;
  description?: string;
  notes?: string;
}

const bySortOrder = <T extends { sort_order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.sort_order - b.sort_order);

/**
 * Build the insert payload for "Save as template".
 *
 * Deliberately does NOT copy `quote.notes`: they're about one client, while template
 * notes pre-fill every quote made from the template. Template notes are typed on the
 * template instead.
 */
export function quoteToTemplateInput(quote: QuoteRow, details: TemplateDetails): CreateTemplateInput {
  const items = bySortOrder(quote.line_items ?? []);
  return {
    name: details.name.trim(),
    description: details.description?.trim() || undefined,
    notes: details.notes?.trim() || null,
    service_type: effectiveServiceType(quote.service_type, quote.fulfillment_type),
    guest_count: quote.guest_count >= 1 ? quote.guest_count : null,
    event_type_id: quote.event_type_id,
    items: items.map((li, i) => ({
      menu_item_id: li.menu_item_id,
      package_id: li.package_id,
      name: li.name,
      description: li.description,
      pricing_type: li.pricing_type,
      unit_price: Number(li.unit_price),
      quantity: li.quantity,
      sort_order: i,
    })),
  };
}

export interface TemplatePrefill {
  lineItems: LineItemDraft[];
  serviceType: string;
  guestCount: number;
  /** null = leave the form's value alone */
  eventTypeId: string | null;
  /** null = leave the form's value alone */
  notes: string | null;
}

/**
 * Map a template onto the quote form.
 *
 * "start" (Templates tab → Use template, fresh form): the template's guest count wins,
 * falling back to `fallbackGuestCount` when it has none.
 * "merge" (in-form Load Template): the form's current guest count (`fallbackGuestCount`)
 * always wins, so a count the user typed is never replaced.
 * Either way per-person lines are sized to the resulting guest count.
 */
export function templateToFormPrefill(
  t: QuoteTemplateRow,
  fallbackGuestCount: number,
  mode: "start" | "merge",
): TemplatePrefill {
  const guestCount = mode === "start" ? (t.guest_count ?? fallbackGuestCount) : fallbackGuestCount;
  const lineItems = templateItemsToDrafts(t.items).map((li) =>
    li.pricing_type === "per_person" ? { ...li, quantity: guestCount, line_total: lineTotal(li.unit_price, guestCount) } : li,
  );
  return {
    lineItems,
    serviceType: t.service_type || "drop_off",
    guestCount,
    eventTypeId: t.event_type_id ?? null,
    notes: t.notes?.trim() || null,
  };
}

/** A template's stored items as editable rows (Templates tab → Edit, #847). Stored quantities are kept. */
export function templateItemsToDrafts(items: QuoteTemplateItemRow[] | undefined): LineItemDraft[] {
  return bySortOrder(items ?? []).map((it) => {
    const unitPrice = Number(it.unit_price);
    return {
      menu_item_id: it.menu_item_id ?? undefined,
      package_id: it.package_id ?? undefined,
      name: it.name,
      description: it.description ?? "",
      pricing_type: it.pricing_type,
      unit_price: unitPrice,
      quantity: it.quantity,
      line_total: lineTotal(unitPrice, it.quantity),
    };
  });
}

/** Edited rows back to template items, `sort_order` taken from their position in the list. */
export function draftsToTemplateItems(drafts: LineItemDraft[]): CreateTemplateInput["items"] {
  return drafts.map((li, i) => ({
    menu_item_id: li.menu_item_id ?? null,
    package_id: li.package_id ?? null,
    name: li.name,
    description: li.description.trim() || null,
    pricing_type: li.pricing_type,
    unit_price: li.unit_price,
    quantity: li.quantity,
    sort_order: i,
  }));
}

/** Blank → null (no guest count); a whole number ≥ 1 → that number; anything else → undefined (invalid). */
export function parseTemplateGuestCount(value: string): number | null | undefined {
  const v = value.trim();
  if (!v) return null;
  if (!/^\d+$/.test(v)) return undefined;
  const n = Number.parseInt(v, 10);
  return n >= 1 ? n : undefined;
}
